import { AnimatePresence, motion } from "motion/react";
import { bool, func, object, string } from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";

import styled, { css } from "styled-components/macro";

import { useGetPermissions } from "Flocknote/API/queries";
import { usePermissions } from "Flocknote/apiHooks";
import { useIsTransparentBgRoute } from "Flocknote/state";
import { useHasAnyElevatedPermissions } from "Flocknote/stateHooks";

import AddMember from "NavBar/TopArea/Toolbar/AddMember";
import SendEmail from "NavBar/TopArea/Toolbar/SendEmail";
import SendText from "NavBar/TopArea/Toolbar/SendText";
import SuperSearch from "NavBar/TopArea/Toolbar/SuperSearch";
import {
  supersearchWidth,
  toolbarButtonSize,
  toolbarPadding,
} from "NavBar/styleConstants";

import FnrIconButton from "UI/FnrIconButton";
import FnrOpacityToggle from "UI/FnrOpacityToggle";
import { marketingColors, navAccentColors, navColors } from "UI/colors";

import { addOpacity } from "Utilities/Colors/colorUtils";
import flash from "Utilities/interpolations/flash";
import { deepShadow } from "Utilities/interpolations/shadow";
import useWanderingGlow from "Utilities/interpolations/useWanderingGlow";

import useIsFlashing from "../useIsFlashing";

const mostlyBlack = addOpacity("#000000", 0.5);
const transparentBlack = addOpacity("#000000", 0);
const stagger = 0.05;
const duration = 0.3;
const expandButtonSize = 44;
const expandButtonMargin = toolbarPadding;
const toolbarOuterHeight = toolbarButtonSize + toolbarPadding * 2 + 2;
const soloPillHeight = `${expandButtonSize}px`;
const groupedPillHeight = `${toolbarButtonSize}px`;

const FloatingToolbar = ({
  isExpanded,
  isFullScreen,
  isComposerMinimized,
  navBarIsFullyLoaded,
  appIsReady,
  handleClick,
  showGroupsHint,
  activeSectionKey,
  superSearchState,
  navToggleRef,
}) => {
  const { isInitialLoading } = useGetPermissions();
  const [{ canSendNotes, canAddMember, canSuperSearch }] = usePermissions();
  const hasAnyElevatedPermissions = useHasAnyElevatedPermissions();
  const isTransparentBg = useIsTransparentBgRoute();
  const isFlashing = useIsFlashing();
  const { isActive: searchIsActive } = superSearchState;

  const [searchUIExpanded, setSearchUIExpanded] = useState(false);
  useEffect(() => {
    if (searchIsActive) {
      const activateTimerId = setTimeout(() => setSearchUIExpanded(true), 400);
      return () => clearTimeout(activateTimerId);
    }
    const deactivateTimerId = setTimeout(() => setSearchUIExpanded(false), 400);
    return () => clearTimeout(deactivateTimerId);
  }, [searchIsActive]);

  const [toolbarAtTop, setToolbarAtTop] = useState(false);
  useEffect(() => {
    if (searchIsActive) {
      setToolbarAtTop(true);
    } else {
      setToolbarAtTop(false);
    }
  }, [searchIsActive]);

  const wrappedSetIsActive = useCallback(
    (val) => {
      if (val) {
        const input = document.querySelector(".super_search_input");
        if (input) input.focus();
      }
      superSearchState.setIsActive(val);
    },
    [superSearchState]
  );

  const show = useMemo(() => {
    if (!isFullScreen) return false;
    if (isComposerMinimized) return false;
    if (!navBarIsFullyLoaded) return false;
    if (!appIsReady) return false;
    return true;
  }, [isFullScreen, isComposerMinimized, navBarIsFullyLoaded, appIsReady]);

  const numberOfToolbarButtons = useMemo(() => {
    let theNumber = 0;
    if (canSendNotes) theNumber += 2;
    if (canAddMember) theNumber += 1;
    return theNumber;
  }, [canAddMember, canSendNotes]);

  const isSoloExpandButton = numberOfToolbarButtons === 0 && !canSuperSearch;
  const expandButtonRightMargin =
    searchUIExpanded || isSoloExpandButton ? 0 : expandButtonMargin;

  const wrapperAnimate = useMemo(() => {
    const wrapperBackgroundColor = isInitialLoading
      ? transparentBlack
      : mostlyBlack;
    const trailingExpandMargin = isSoloExpandButton ? 0 : expandButtonMargin;
    const width = searchUIExpanded
      ? supersearchWidth + toolbarPadding * 2
      : toolbarButtonSize * numberOfToolbarButtons +
        (canSuperSearch ? toolbarButtonSize : 0) +
        expandButtonSize +
        trailingExpandMargin +
        toolbarPadding * numberOfToolbarButtons;
    return { backgroundColor: wrapperBackgroundColor, width };
  }, [
    isInitialLoading,
    numberOfToolbarButtons,
    canSuperSearch,
    isSoloExpandButton,
    searchUIExpanded,
  ]);

  const toolbarButtonAnimate = useMemo(
    () =>
      searchUIExpanded
        ? { width: 0, scale: 0 }
        : { width: toolbarButtonSize, scale: 1 },
    [searchUIExpanded]
  );

  const backgroundColor = useMemo(() => {
    if (navColors[activeSectionKey]) return navColors[activeSectionKey];
    return navColors.groups;
  }, [activeSectionKey]);

  const glowStyle = useWanderingGlow(
    navAccentColors[activeSectionKey]?.[1] || navAccentColors.groups[1],
    { blur: 10, spread: 4, minOpacity: 0.25, maxOpacity: 0.6 }
  );

  // Use visualViewport.height to avoid iOS address bar inflation of innerHeight,
  // but ignore keyboard-driven shrinkage (when vvH << innerHeight).
  const getViewportHeight = () => {
    const vvH = window.visualViewport?.height;
    const iH = window.innerHeight;
    if (vvH && iH - vvH < 100) return vvH;
    return iH;
  };

  const [viewportHeight, setViewportHeight] = useState(getViewportHeight);
  useEffect(() => {
    const handler = () => setViewportHeight(getViewportHeight());
    window.addEventListener("resize", handler);
    window.visualViewport?.addEventListener("resize", handler);
    return () => {
      window.removeEventListener("resize", handler);
      window.visualViewport?.removeEventListener("resize", handler);
    };
  }, []);

  const fixedBottom = useMemo(() => {
    if (toolbarAtTop) return viewportHeight - 12 - toolbarOuterHeight;
    return 12;
  }, [toolbarAtTop, viewportHeight]);

  if (
    !canSendNotes &&
    !canAddMember &&
    !canSuperSearch &&
    !hasAnyElevatedPermissions
  )
    return null;
  if (!show) return null;

  return (
    <FnrOpacityToggle show={show} delayIn={450}>
      <FixedWrapper
        data-cy="NavBar_FloatingToolbar"
        initial={false}
        animate={{ bottom: fixedBottom }}
        transition={{ type: "spring", stiffness: 110, damping: 22, mass: 1.5 }}
      >
        <PillWrapper
          $isSoloExpandButton={isSoloExpandButton}
          initial={false}
          animate={wrapperAnimate}
          transition={{ duration: 0.4 }}
        >
          <ExpandButtonMotion
            initial={false}
            animate={{
              width: searchUIExpanded ? 0 : expandButtonSize,
              marginRight: expandButtonRightMargin,
              scale: searchUIExpanded ? 0 : 1,
            }}
            transition={{
              duration,
              delay: searchUIExpanded ? 0 : stagger * 3,
            }}
          >
            <ExpandButton
              ref={navToggleRef}
              $isFlashing={isFlashing || showGroupsHint}
              $backgroundColor={backgroundColor}
              style={glowStyle}
              data-flashing={isFlashing || undefined}
            >
              <FnrIconButton
                icon="bars fas"
                aria-label={
                  isExpanded
                    ? "collapse-navigation-menu"
                    : "open-navigation-menu"
                }
                aria-expanded={isExpanded}
                onClick={handleClick}
                backgroundColor="rgba(0, 0, 0, 0.75)"
                hoverFocusBackgroundColor={
                  isTransparentBg && isFlashing
                    ? "rgba(0, 0, 0, 0.65)"
                    : isTransparentBg
                    ? "rgba(0, 0, 0, 0)"
                    : "rgba(0, 0, 0, 0.55)"
                }
                color={
                  navAccentColors[activeSectionKey]?.[0] ||
                  navAccentColors.groups[0]
                }
                hoverFocusColor={
                  navAccentColors[activeSectionKey]?.[0] ||
                  navAccentColors.groups[0]
                }
                scaleIconOnly
                size="md"
              />
            </ExpandButton>
          </ExpandButtonMotion>
          <AnimatePresence>
            {canSendNotes && (
              <ToolbarButtonMotion
                key="floatingEmail"
                initial={false}
                animate={{
                  ...toolbarButtonAnimate,
                  marginRight: searchUIExpanded ? 0 : toolbarPadding / 2,
                }}
                transition={{
                  duration,
                  delay: searchUIExpanded ? stagger : stagger * 2,
                }}
              >
                <SendEmail />
              </ToolbarButtonMotion>
            )}
            {canSendNotes && (
              <ToolbarButtonMotion
                key="floatingText"
                initial={false}
                animate={{
                  ...toolbarButtonAnimate,
                  marginLeft: searchUIExpanded ? 0 : toolbarPadding / 2,
                  marginRight: searchUIExpanded
                    ? 0
                    : toolbarPadding / (canAddMember ? 2 : 1),
                }}
                transition={{
                  duration,
                  delay: searchUIExpanded ? stagger * 2 : stagger,
                }}
              >
                <SendText />
              </ToolbarButtonMotion>
            )}
            {canAddMember && (
              <ToolbarButtonMotion
                key="floatingAddMember"
                initial={false}
                animate={{
                  ...toolbarButtonAnimate,
                  marginLeft:
                    searchUIExpanded || !canSendNotes ? 0 : toolbarPadding / 2,
                  marginRight: searchUIExpanded ? 0 : toolbarPadding,
                }}
                transition={{
                  duration,
                  delay: searchUIExpanded ? stagger * 3 : 0,
                }}
              >
                <AddMember isNavToolbar />
              </ToolbarButtonMotion>
            )}
            {canSuperSearch && (
              <SuperSearchWrapper $isActive={searchUIExpanded}>
                <SuperSearch
                  {...superSearchState}
                  setIsActive={wrappedSetIsActive}
                  isActive={searchUIExpanded}
                />
              </SuperSearchWrapper>
            )}
          </AnimatePresence>
        </PillWrapper>
      </FixedWrapper>
    </FnrOpacityToggle>
  );
};

FloatingToolbar.propTypes = {
  isExpanded: bool.isRequired,
  isFullScreen: bool.isRequired,
  isComposerMinimized: bool.isRequired,
  navBarIsFullyLoaded: bool,
  appIsReady: bool,
  handleClick: func.isRequired,
  showGroupsHint: bool,
  activeSectionKey: string,
  superSearchState: object.isRequired,
  navToggleRef: object,
};

const FixedWrapper = styled(motion.div)`
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2200;
  pointer-events: auto;
`;

const soloPillStyles = css`
  padding: 0px;
  height: ${soloPillHeight};
  border: none;
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
`;

const groupedPillStyles = css`
  padding: ${toolbarPadding}px;
  height: ${groupedPillHeight};
  ${deepShadow}
  border: 1px solid ${addOpacity(marketingColors.gray, 0.15)};
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
`;

const PillWrapper = styled(motion.div)`
  display: flex;
  align-items: center;
  border-radius: ${toolbarPadding + toolbarButtonSize}px;
  ${({ $isSoloExpandButton }) =>
    $isSoloExpandButton ? soloPillStyles : groupedPillStyles}
`;

const ExpandButtonMotion = styled(motion.div)`
  flex-shrink: 0;
`;

const ExpandButton = styled.div`
  width: ${expandButtonSize}px;
  height: ${expandButtonSize}px;
  flex-shrink: 0;
  border-radius: 50%;
  overflow: hidden;
  cursor: pointer;
  border: 1px solid rgba(255, 255, 255, 0.2);
  ${flash}

  button {
    height: ${expandButtonSize}px;
    width: ${expandButtonSize}px;
  }
`;

const ToolbarButtonMotion = styled(motion.div)`
  display: flex;
  align-items: center;
  transform-origin: center;
`;

const SuperSearchWrapper = styled.div`
  overflow: hidden;
  display: flex;
  align-items: center;
  border-radius: ${toolbarPadding + toolbarButtonSize}px;
  flex-grow: ${({ $isActive }) => ($isActive ? 1 : 0)};
  flex-shrink: 0;
  flex-basis: ${({ $isActive }) =>
    $isActive ? "0%" : `${toolbarButtonSize}px`};
  transition: flex-grow 0.4s ease, flex-basis 0.4s ease;
`;

export default FloatingToolbar;
